import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { AlertTriangle, CalendarClock, ListChecks, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from 'recharts';
import { purchaseOrdersAPI } from '../../services/api';
import { formatCurrency } from '../../utils/poFormatters.jsx';

const DELIVERY_TYPE_LABELS = {
  session: 'Private Delivery Session',
  partner_conference: 'Partner Conference',
  regional_in_person: 'Regional In-Person',
};

const WORKLOAD_COLORS = ['#84cc16', '#3b82f6', '#f59e0b', '#8b5cf6', '#14b8a6', '#9CA3AF'];

// Lean 4-metric PO Dashboard: total/consumed/remaining, POs expiring soon, line items due
// soon, and the mismatch queue — all scoped to Delivery POs.
export const PODashboardTab = ({ pos, totalValue, totalUsed, totalAvailable, combinedPos = [], workloadTypeOptions = [] }) => {
  const [mismatchQueue, setMismatchQueue] = useState([]);
  const [loadingMismatch, setLoadingMismatch] = useState(true);

  useEffect(() => {
    purchaseOrdersAPI.getMismatchQueue()
      .then(setMismatchQueue)
      .catch(() => setMismatchQueue([]))
      .finally(() => setLoadingMismatch(false));
  }, []);

  const today = new Date();
  const in30Days = new Date(today.getTime() + 30 * 86400000);
  const in15Days = new Date(today.getTime() + 15 * 86400000);

  const posExpiringSoon = (combinedPos || [])
    .filter(po => po.validity_expiring_soon)
    .sort((a, b) => (a.validity_days_left ?? 999) - (b.validity_days_left ?? 999));

  const lineItemsDueSoon = [];
  (combinedPos || []).forEach(po => {
    (po.line_items || []).forEach(li => {
      if (!li.delivery_date || li.delivered) return;
      const d = new Date(li.delivery_date);
      if (d >= today && d <= in15Days) {
        lineItemsDueSoon.push({ ...li, po_number: po.po_number, po_short_title: po.short_title, remaining: li.amount || 0 });
      }
    });
  });
  lineItemsDueSoon.sort((a, b) => new Date(a.delivery_date) - new Date(b.delivery_date));

  const workloadChartData = workloadTypeOptions.map(opt => {
    const matching = combinedPos.filter(po => po.workload_type === opt.value);
    return {
      name: opt.label,
      value: matching.reduce((sum, po) => sum + (po.consolidated_total_amount ?? po.total_amount ?? 0), 0),
      count: matching.length,
    };
  });
  const untaggedPos = combinedPos.filter(po => !po.workload_type);
  if (untaggedPos.length > 0) {
    workloadChartData.push({
      name: 'Untagged',
      value: untaggedPos.reduce((sum, po) => sum + (po.consolidated_total_amount ?? po.total_amount ?? 0), 0),
      count: untaggedPos.length,
    });
  }

  return (
    <div className="space-y-6" data-testid="po-dashboard-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2 flex-row items-center gap-2 space-y-0">
            <CalendarClock className="w-4 h-4 text-amber-600" />
            <CardTitle className="text-sm">POs Expiring in 30 Days</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold" data-testid="po-dashboard-expiring-count">{posExpiringSoon.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2 flex-row items-center gap-2 space-y-0">
            <ListChecks className="w-4 h-4 text-blue-600" />
            <CardTitle className="text-sm">Line Items Due in 15 Days</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold" data-testid="po-dashboard-line-items-due-count">{lineItemsDueSoon.length}</p>
          </CardContent>
        </Card>
      </div>

      {workloadChartData.length > 0 && (
        <Card data-testid="po-workload-split-chart">
          <CardHeader><CardTitle className="text-base">Total PO Value by Workload Type</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={workloadChartData} margin={{ top: 4, right: 12, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatCurrency(v)} width={70} />
                <RechartsTooltip formatter={(v, key, entry) => [`${formatCurrency(v)} (${entry.payload.count} PO${entry.payload.count === 1 ? '' : 's'})`, 'Total Value']} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {workloadChartData.map((entry, idx) => (
                    <Cell key={entry.name} fill={WORKLOAD_COLORS[idx % WORKLOAD_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">POs Expiring Soon</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {posExpiringSoon.length === 0 ? (
            <p className="text-sm text-muted-foreground">No POs expiring in the next 30 days.</p>
          ) : posExpiringSoon.map(po => (
            <div key={po.id} className="flex items-center justify-between border rounded-lg px-3 py-2 text-sm" data-testid={`po-expiring-row-${po.id}`}>
              <span>{po.po_number}{po.short_title ? ` — ${po.short_title}` : ''}</span>
              <Badge variant="outline">{po.validity_days_left === 0 ? 'Expires today' : `${po.validity_days_left}d left`} · {po.validity_date}</Badge>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Line Items Due Soon</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {lineItemsDueSoon.length === 0 ? (
            <p className="text-sm text-muted-foreground">No planned line items due in the next 15 days.</p>
          ) : lineItemsDueSoon.map((li, i) => (
            <div key={li.id || i} className="flex items-center justify-between border rounded-lg px-3 py-2 text-sm" data-testid={`line-item-due-row-${i}`}>
              <div className="min-w-0">
                <p className="truncate">{li.description} <span className="text-muted-foreground">({li.po_number})</span></p>
                <p className="text-xs text-muted-foreground">Due {li.delivery_date} · {formatCurrency(li.remaining)} remaining</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" />Mismatch Queue</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {loadingMismatch ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Checking deliveries...</div>
          ) : mismatchQueue.length === 0 ? (
            <p className="text-sm text-muted-foreground">No mismatch warnings — all linked deliveries look consistent with their PO line items.</p>
          ) : mismatchQueue.map((item, i) => (
            <div key={i} className="border border-amber-200 bg-amber-50 rounded-lg p-3 space-y-1" data-testid={`mismatch-queue-row-${i}`}>
              <div className="flex items-center justify-between text-sm font-medium">
                <span>{item.delivery_title} <Badge variant="outline" className="ml-1 text-xs">{DELIVERY_TYPE_LABELS[item.delivery_type] || item.delivery_type}</Badge></span>
                <span className="text-muted-foreground text-xs">{item.po_number}</span>
              </div>
              {item.warnings.map((w, wi) => (
                <p key={wi} className="text-xs text-amber-800">{w}</p>
              ))}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default PODashboardTab;
